import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/site/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import Seo from "@/components/site/Seo";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [sp, setSp] = useSearchParams();
  const [cat, setCat] = useState(sp.get("category") || "all");
  const [brand, setBrand] = useState("all");
  const [cond, setCond] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/brands").then(r => setBrands(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (sort) params.sort = sort;
    if (cat !== "all") {
      const c = categories.find(x => x.name === cat);
      if (c) params.category_id = c.id;
    }
    if (brand !== "all") {
      const b = brands.find(x => x.name === brand);
      if (b) params.brand_id = b.id;
    }
    if (cond !== "all") params.condition = cond;
    api.get("/products", { params }).then(r => setProducts(r.data));
  }, [q, cat, brand, cond, sort, categories, brands]);

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <Seo page="products"/>
      <div className="bg-[#0F172A] text-white py-10">
        <div className="container-x">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Products</h1>
          <p className="text-slate-300 mt-1">Wholesale IT products. Browse and add to your enquiry.</p>
        </div>
      </div>
      <div className="container-x py-8">
        <div className="bg-white border border-[#E2E8F0] rounded-md p-4 flex flex-wrap gap-3 items-center mb-6" data-testid="products-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search product, SKU, brand..." className="pl-9" data-testid="products-search"/>
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-40" data-testid="filter-brand"><SelectValue placeholder="Brand"/></SelectTrigger>
            <SelectContent><SelectItem value="all">All Brands</SelectItem>
              {brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cond} onValueChange={setCond}>
            <SelectTrigger className="w-40" data-testid="filter-condition"><SelectValue placeholder="Condition"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Condition</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Refurbished">Refurbished</SelectItem>
              <SelectItem value="Used">Used</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40" data-testid="filter-sort"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="name">Name (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {products.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-md p-12 text-center">
            <div className="font-heading font-bold text-lg">No products found</div>
            <p className="text-slate-500 mt-1">Try another search or contact REXTECH for availability.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map(p => <ProductCard key={p.id} product={p}/>)}
          </div>
        )}
      </div>
    </div>
  );
}
